import { StepFlowShell } from '@/components/step-flow/StepFlowShell';
import type { StepFlowLang } from '@/components/step-flow/StepFlowShell.types';
import { PiiIllustration } from '@/pages/pii-training/PiiIllustration';
import { PiiTrainingStepBody } from '@/pages/pii-training/PiiTrainingStepBody';
import type { PiiTrainingFormValues } from '@/pages/pii-training/piiTraining.types';
import { PII_TRAINING_TOTAL_STEPS, piiTrainingContent } from '@/pages/pii-training/piiTrainingContent';
import { useCompletePiiTraining } from '@/services/piiTraining/useCompletePiiTraining';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

const DEFAULT_VALUES: PiiTrainingFormValues = {
  example1: null,
  example2: null,
  example3: null,
  consented: false,
};

const QUIZ_STEP = 3;

const PiiTrainingPage = () => {
  const [lang, setLang] = useState<StepFlowLang>('is');
  const [step, setStep] = useState(1);
  const copy = piiTrainingContent[lang];

  const { control, handleSubmit } = useForm<PiiTrainingFormValues>({ defaultValues: DEFAULT_VALUES });
  const [example1, example2, example3, consented] = useWatch({
    control,
    name: ['example1', 'example2', 'example3', 'consented'],
  });
  const completion = useCompletePiiTraining(copy.toast);

  const isLastStep = step === PII_TRAINING_TOTAL_STEPS;
  const allAnswered = [example1, example2, example3].every((answer) => answer !== null);
  const nextDisabled = (step === QUIZ_STEP && !allAnswered) || (isLastStep && (!consented || completion.isSubmitted));
  const stepCopy = [copy.whatIsPii, copy.combinations, copy.quiz, copy.consent][step - 1];

  const submit = handleSubmit((values) =>
    completion.submit({ example1: values.example1, example2: values.example2, example3: values.example3 })
  );

  const handleNext = () => {
    if (isLastStep) {
      submit();
      return;
    }
    setStep((current) => current + 1);
  };

  return (
    <StepFlowShell
      step={step}
      totalSteps={PII_TRAINING_TOTAL_STEPS}
      lang={lang}
      onLangChange={setLang}
      stepLabel={copy.stepLabel(step, PII_TRAINING_TOTAL_STEPS)}
      title={stepCopy.title}
      subtitle={stepCopy.subtitle}
      illustration={<PiiIllustration step={step} copy={copy} values={{ example1, example2, example3, consented }} />}
      onBack={step > 1 ? () => setStep((current) => current - 1) : undefined}
      backLabel={copy.nav.back}
      onNext={handleNext}
      nextLabel={isLastStep ? (completion.isPending ? copy.nav.submitting : copy.nav.complete) : copy.nav.next}
      nextDisabled={nextDisabled}
      nextLoading={completion.isPending}
    >
      <PiiTrainingStepBody step={step} copy={copy} control={control} />
    </StepFlowShell>
  );
};

export default PiiTrainingPage;
